<template src="./keywords-to-insert-batch-details.component.html"></template>
<style scoped src="./keywords-to-insert-batch-details.component.css"></style>
<script lang="ts">
import Vue from 'vue';
import axios from 'axios';
import Sidebar from '@/components/sidebar/sidebar.component.vue';
import api from '@/api';
import {getErrorMessage, noop} from '@/helpers';
import {toasted} from '@/plugins/toasted';
import type {KeywordsToInsertBatchKeywordRow} from '@/models';

type KeywordsToInsertBatchKeywordsResponse = {
  rows: unknown[]
};

const SORTABLE_BATCH_KEYWORDS_COLUMNS = [
  'keyword',
  'market',
  'masking',
  'images',
  'visymoArticle',
] as const;

type BatchKeywordsSortColumn = typeof SORTABLE_BATCH_KEYWORDS_COLUMNS[number];
type SortDirection = 'asc' | 'desc';
type StatusFilterValue = '' | '1' | '0';
type BatchKeywordsTableFilter = {
  keyword: string
  market: string
  masking: StatusFilterValue
  images: StatusFilterValue
  visymoArticle: StatusFilterValue
};
type BatchKeywordsSorting = {
  column: BatchKeywordsSortColumn
  direction: SortDirection
};

const DEFAULT_SORT_COLUMN = 'keyword';
const DEFAULT_SORT_DIRECTION: SortDirection = 'desc';

function getQueryParamValue(query: Record<string, any>, key: string) {
  const value = query[key];
  if (Array.isArray(value)) return String(value[0] ?? '');
  return typeof value === 'string' ? value : '';
}

function normalizeSortColumn(column: string): BatchKeywordsSortColumn {
  return SORTABLE_BATCH_KEYWORDS_COLUMNS.includes(column as BatchKeywordsSortColumn)
    ? column as BatchKeywordsSortColumn
    : DEFAULT_SORT_COLUMN;
}

function normalizeSortDirection(direction: string): SortDirection {
  return direction === 'asc' ? 'asc' : DEFAULT_SORT_DIRECTION;
}

function normalizeStatusFilterValue(value: string): StatusFilterValue {
  return value === '1' || value === '0' ? value : '';
}

function toBinaryInt(...values: unknown[]) {
  const value = values.find(v => v !== null && v !== undefined);
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'number') return value > 0 ? 1 : 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'done', 'masked'].includes(normalized)) return 1;
    const numeric = Number(normalized);
    if (!Number.isNaN(numeric)) return numeric > 0 ? 1 : 0;
  }
  return 0;
}

function normalizeBatchKeywordRow(row: Record<string, unknown>): KeywordsToInsertBatchKeywordRow {
  return {
    keyword: String(row.keyword ?? ''),
    market: String(row.market ?? '').trim().toUpperCase(),
    masking: toBinaryInt(row.masking, row.masked, row.masking_status),
    images: toBinaryInt(row.images, row.images_count, row.image_count),
    visymoArticle: toBinaryInt(row.visymoArticle, row.visymo_article, row.visymo_articles),
  };
}

export default Vue.extend({
  components: {
    Sidebar,
  },
  data() {
    return {
      ac: null as AbortController | null,
      batchId: '',
      rows: [] as KeywordsToInsertBatchKeywordRow[],
      sorting: {
        column: DEFAULT_SORT_COLUMN as BatchKeywordsSortColumn,
        direction: DEFAULT_SORT_DIRECTION as SortDirection,
      } as BatchKeywordsSorting,
      filters: {
        keyword: '',
        market: '',
        masking: '',
        images: '',
        visymoArticle: '',
      } as BatchKeywordsTableFilter,
      showLoading: false,
      tableError: '',
    };
  },
  computed: {
    marketOptions() {
      return Array.from(new Set(this.rows.map(row => row.market).filter(Boolean)))
        .sort((a, b) => a.localeCompare(b));
    },
    filteredRows(): KeywordsToInsertBatchKeywordRow[] {
      const keywordFilter = this.filters.keyword.trim().toLowerCase();
      return this.rows.filter(row => {
        if (keywordFilter && !row.keyword.toLowerCase().includes(keywordFilter)) {
          return false;
        }
        if (this.filters.market && row.market !== this.filters.market) {
          return false;
        }
        if (this.filters.masking && String(row.masking) !== this.filters.masking) {
          return false;
        }
        if (this.filters.images && String(row.images) !== this.filters.images) {
          return false;
        }
        if (this.filters.visymoArticle && String(row.visymoArticle) !== this.filters.visymoArticle) {
          return false;
        }
        return true;
      });
    },
    displayRows(): KeywordsToInsertBatchKeywordRow[] {
      const rows = [...this.filteredRows];
      const factor = this.sorting.direction === 'asc' ? 1 : -1;
      rows.sort((a, b) => {
        const column = this.sorting.column;
        if (column === 'keyword' || column === 'market') {
          const left = String(a[column]).toLowerCase();
          const right = String(b[column]).toLowerCase();
          const result = left.localeCompare(right);
          if (result !== 0) return result * factor;
        } else {
          const result = a[column] - b[column];
          if (result !== 0) return result * factor;
        }
        return a.keyword.localeCompare(b.keyword);
      });
      return rows;
    },
  },
  watch: {
    '$route.params.batchId': {
      async handler() {
        await this.getBatchKeywords();
      },
      immediate: true,
    },
    '$route.query': {
      handler(query: Record<string, any>) {
        this.loadStateFromQuery(query);
        this.syncQueryWithState(true);
      },
      immediate: true,
    },
  },
  mounted() {
    document.body.classList.add(
      'min-w-site',
      'bg-40',
      'text-90',
      'font-medium',
      'min-h-full',
    );
  },
  destroyed() {
    document.body.classList.remove(
      'min-w-site',
      'bg-40',
      'text-90',
      'font-medium',
      'min-h-full',
    );
  },
  methods: {
    async getBatchKeywords() {
      this.batchId = String(this.$route.params.batchId || '');
      this.showLoading = true;
      this.tableError = '';
      try {
        this.ac?.abort();
        this.ac = new AbortController();
        const response = await api.execute(
          'get',
          `/keywords-to-insert/batches/${encodeURIComponent(this.batchId)}/keywords`,
          null,
          null,
          this.ac.signal,
        ) as KeywordsToInsertBatchKeywordsResponse | unknown[];
        const rows = Array.isArray(response)
          ? response
          : response?.rows ?? [];
        this.rows = rows.map(row => normalizeBatchKeywordRow(row as Record<string, unknown>));
      } catch (e) {
        console.error(e);
        if (!axios.isCancel(e)) {
          this.tableError = getErrorMessage(e);
          toasted.showErrorMessage(this.tableError);
        }
      } finally {
        this.showLoading = false;
      }
    },
    loadStateFromQuery(query: Record<string, any>) {
      this.sorting = {
        column: normalizeSortColumn(getQueryParamValue(query, 'column')),
        direction: normalizeSortDirection(getQueryParamValue(query, 'direction')),
      };
      this.filters = {
        keyword: getQueryParamValue(query, 'keyword').trim(),
        market: getQueryParamValue(query, 'market').trim().toUpperCase(),
        masking: normalizeStatusFilterValue(getQueryParamValue(query, 'masking')),
        images: normalizeStatusFilterValue(getQueryParamValue(query, 'images')),
        visymoArticle: normalizeStatusFilterValue(getQueryParamValue(query, 'visymoArticle')),
      };
    },
    buildQueryFromState() {
      const query: Record<string, string> = {
        column: this.sorting.column,
        direction: this.sorting.direction,
      };
      if (this.filters.keyword.trim()) query.keyword = this.filters.keyword.trim();
      if (this.filters.market.trim()) query.market = this.filters.market.trim().toUpperCase();
      if (this.filters.masking) query.masking = this.filters.masking;
      if (this.filters.images) query.images = this.filters.images;
      if (this.filters.visymoArticle) query.visymoArticle = this.filters.visymoArticle;
      return query;
    },
    buildQueryFromRoute(query: Record<string, any>) {
      const normalized = {
        column: normalizeSortColumn(getQueryParamValue(query, 'column')),
        direction: normalizeSortDirection(getQueryParamValue(query, 'direction')),
        keyword: getQueryParamValue(query, 'keyword').trim(),
        market: getQueryParamValue(query, 'market').trim().toUpperCase(),
        masking: normalizeStatusFilterValue(getQueryParamValue(query, 'masking')),
        images: normalizeStatusFilterValue(getQueryParamValue(query, 'images')),
        visymoArticle: normalizeStatusFilterValue(getQueryParamValue(query, 'visymoArticle')),
      };
      const normalizedQuery: Record<string, string> = {
        column: normalized.column,
        direction: normalized.direction,
      };
      if (normalized.keyword) normalizedQuery.keyword = normalized.keyword;
      if (normalized.market) normalizedQuery.market = normalized.market;
      if (normalized.masking) normalizedQuery.masking = normalized.masking;
      if (normalized.images) normalizedQuery.images = normalized.images;
      if (normalized.visymoArticle) normalizedQuery.visymoArticle = normalized.visymoArticle;
      return normalizedQuery;
    },
    serializeQuery(query: Record<string, string>) {
      const searchParams = new URLSearchParams();
      for (const key of Object.keys(query).sort()) {
        searchParams.append(key, String(query[key]));
      }
      return searchParams.toString();
    },
    syncQueryWithState(replace: boolean = false) {
      const targetQuery = this.buildQueryFromState();
      const currentQuery = this.buildQueryFromRoute(this.$route.query);
      if (this.serializeQuery(targetQuery) === this.serializeQuery(currentQuery)) return;
      this.updateQueryParams(targetQuery, replace);
    },
    updateQueryParams(query: Record<string, string>, replace: boolean = false) {
      if (replace) {
        this.$router.replace({query}).catch(noop);
      } else {
        this.$router.push({query}).catch(noop);
      }
    },
    onSort(column: BatchKeywordsSortColumn) {
      if (this.sorting.column === column) {
        this.sorting.direction = this.sorting.direction === 'desc' ? 'asc' : 'desc';
      } else {
        this.sorting.column = column;
        this.sorting.direction = DEFAULT_SORT_DIRECTION;
      }
      this.syncQueryWithState();
    },
    onKeywordInput() {
      this.syncQueryWithState();
    },
    onFilterSelect() {
      this.syncQueryWithState();
    },
    getSortIcon(column: BatchKeywordsSortColumn) {
      if (this.sorting.column !== column) return '';
      return this.sorting.direction === 'asc' ? '▲' : '▼';
    },
    getMarketFlag(market: string) {
      const normalized = market.trim().toUpperCase();
      if (!/^[A-Z]{2}$/.test(normalized)) return '';
      const codePoints = Array.from(normalized).map(char => 127397 + char.charCodeAt(0));
      return String.fromCodePoint(...codePoints);
    },
    formatCell(value: number) {
      return value === 1 ? '✅' : '⏳';
    },
  },
});
</script>
